// web/src/components/lessons/LessonGrid.jsx
// Riders × days grid. Horizontally scrollable; the rider column is sticky.
// Each cell shows that rider's lessons for the day — tap a lesson to edit, tap
// an empty cell to add. The current day's column is tinted.

import { fmtDate } from '../../lib/dates';

const COLS = '9rem repeat(7, minmax(0, 1fr))';

export default function LessonGrid({
  days,
  today,
  people,
  byRiderDate,
  canEdit,
  onCell,
  onRemoveRider,
  onRiderName,
}) {
  return (
    <div className="card overflow-x-auto">
      <div className="min-w-[56rem]">
        {/* header */}
        <div className="grid border-b border-ink-100" style={{ gridTemplateColumns: COLS }}>
          <div className="sticky left-0 z-10 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
            Rider
          </div>
          {days.map((d) => (
            <div key={d} className={`px-2 py-2 text-center ${d === today ? 'bg-emerald-50' : ''}`}>
              <div className="text-xs font-semibold text-ink-700">{weekday(d)}</div>
              <div className="text-xs text-ink-400 tabular-nums">{dayNum(d)}</div>
            </div>
          ))}
        </div>

        {/* rider rows */}
        {people.map((p) => (
          <div
            key={p.id}
            className="grid border-b border-ink-100 last:border-0"
            style={{ gridTemplateColumns: COLS }}
          >
            <div className="sticky left-0 z-10 bg-white px-3 py-2 flex items-start justify-between gap-1">
              {/* The name is the way into "everything this rider has on" when
                  the grid is embedded in the Schedule board; plain text when
                  there's nowhere for it to lead. */}
              {onRiderName ? (
                <button
                  onClick={() => onRiderName(p)}
                  className="text-sm font-medium text-ink-800 truncate text-left hover:underline"
                  title={`Show ${p.name}'s schedule`}
                >
                  {p.name}
                </button>
              ) : (
                <span className="text-sm font-medium text-ink-800 truncate">{p.name}</span>
              )}
              {onRemoveRider && (
                <button
                  onClick={() => onRemoveRider(p)}
                  className="text-ink-200 hover:text-red-600 text-xs shrink-0"
                  aria-label={`Remove ${p.name}`}
                  title="Remove rider"
                >
                  ✕
                </button>
              )}
            </div>
            {days.map((d) => (
              <Cell
                key={d}
                highlight={d === today}
                items={byRiderDate[p.id]?.[d] ?? []}
                canEdit={canEdit}
                onAdd={() => onCell(p.id, p.name, d, null)}
                onEdit={(lesson) => onCell(p.id, p.name, d, lesson)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Cell({ items, highlight, canEdit, onAdd, onEdit }) {
  const clickable = canEdit && items.length === 0;
  return (
    <div
      className={
        `min-h-[3.5rem] border-l border-ink-100 p-1 ` +
        (highlight ? 'bg-emerald-50/40 ' : '') +
        (clickable ? 'cursor-pointer hover:bg-ink-50' : '')
      }
      onClick={clickable ? onAdd : undefined}
    >
      <div className="space-y-1">
        {items.map((l) => (
          <button
            key={l.id}
            onClick={(e) => {
              e.stopPropagation();
              if (canEdit) onEdit(l);
            }}
            className={chipClass(l.status)}
          >
            <span className="font-medium tabular-nums">{formatTime(l.startTime) || 'TBD'}</span>
            {l.horseName ? (
              <span className="ml-1 truncate">{l.horseName}</span>
            ) : typeLabel(l) ? (
              <span className="ml-1 truncate">{typeLabel(l)}</span>
            ) : null}
          </button>
        ))}
        {canEdit && items.length > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            className="block w-full text-left text-xs text-ink-300 hover:text-ink-600 px-1"
          >
            + add
          </button>
        )}
      </div>
    </div>
  );
}

function chipClass(status) {
  const base = 'block w-full text-left text-xs rounded px-1.5 py-1 truncate border ';
  switch (status) {
    case 'cancelled':
      return base + 'bg-ink-50 border-ink-100 text-ink-400 line-through';
    case 'tentative':
      return base + 'bg-amber-50 border-amber-200 border-dashed text-amber-800';
    case 'completed':
      return base + 'bg-emerald-50 border-emerald-200 text-emerald-800';
    default:
      return base + 'bg-white border-ink-200 text-ink-800 hover:border-ink-300';
  }
}

// Lesson types are barn config now, so the name comes down with the lesson
// rather than from a map baked in here. The legacy free-text column is the
// fallback for anything created before types existed.
function typeLabel(l) {
  if (l.lessonTypeName) return l.lessonTypeName;
  return l.lessonType ? l.lessonType.replace(/_/g, ' ') : null;
}

// "HH:MM" (24h) → "9:45 AM".
function formatTime(hhmm) {
  if (!hhmm) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return hhmm;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

function weekday(iso) {
  return fmtDate(iso, 'weekday');
}
function dayNum(iso) {
  return fmtDate(iso, 'numeric');
}
